import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SnackbarService } from '../snackbar/snackbar.service';
import { LigneOrdonnance } from 'src/app/utils/interfaces/new/ligne-ordonnance';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { CreateModeleOrdonnance } from 'src/app/utils/interfaces/new/Create-Modele-Ordonnance';
import { ModeleOrdonnanceResume } from 'src/app/utils/interfaces/new/ModeleOrdonnanceResume';
import { ModeleOrdonnanceProfilResume } from 'src/app/utils/interfaces/new/ModeleOrdonnanceProfilResume';
import { ModeleOrdonnanceDetail } from 'src/app/utils/interfaces/new/ModeleOrdonnanceDetail';
import { LoginService } from '../login/login.service';
@Injectable({
  providedIn: 'root'
})
export class ModeleOrdonnancePanelService {

  private readonly defaultHttpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders(),
    params: undefined
  };

  private apiUri = `${environment.basePath}/consultation/ordonnances/modeles`;

  private modelesPersoCache = new Map<string, Paginated<ModeleOrdonnanceResume>>();
  private profilsCabinetCache: ModeleOrdonnanceProfilResume[];
  private modelesCabinetCache = new Map<string, Paginated<ModeleOrdonnanceResume>>();

  private _modelesChanged = new Subject<void>();
  public modelesChanged$ = this._modelesChanged.asObservable();
  private cacheProfilId: string;

  constructor(
    private _http: HttpClient,
    private _snack: SnackbarService,
    private _login: LoginService
  ) { }

  public async find(currentPage: number = 1, perpage: number = 30, search?: string, forceRefresh: boolean = false): Promise<Paginated<ModeleOrdonnanceResume>> {
    this.checkCacheProfil();
    try {
      const key = this.getCacheKey(currentPage, perpage, search);
      if (!forceRefresh && this.modelesPersoCache.has(key)) {
        return this.modelesPersoCache.get(key);
      }

      let params = new HttpParams()
        .set('currentPage', currentPage.toString())
        .set('perpage', perpage.toString());

      if (search) {
        params = params.set('search', search);
      }

      const res = await this._http.get(this.apiUri, {...this.defaultHttpOptions, params}).toPromise();
      const result: Paginated<ModeleOrdonnanceResume> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: res.body as ModeleOrdonnanceResume[]
      };

      this.modelesPersoCache.set(key, result);
      return result;
    } catch (error) {
      this._snack.openSnackBar(error?.error?.message || 'Erreur lors de la recherche des modèles.');
      throw error;
    }
  }

  public async findProfilsCabinet(): Promise<ModeleOrdonnanceProfilResume[]> {
    this.checkCacheProfil();
    try {
      if (this.profilsCabinetCache) {
        return this.profilsCabinetCache;
      }

      const res = await this._http.get(`${this.apiUri}/cabinet`, this.defaultHttpOptions).toPromise();
      this.profilsCabinetCache = res.body as ModeleOrdonnanceProfilResume[];
      return this.profilsCabinetCache;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findByProfil(idProfil: string, currentPage: number = 1, perpage: number = 30, search?: string): Promise<Paginated<ModeleOrdonnanceResume>> {
    this.checkCacheProfil();
    try {
      const key = `${idProfil}_${this.getCacheKey(currentPage, perpage, search)}`;

      if (this.modelesCabinetCache.has(key)) {
        return this.modelesCabinetCache.get(key);
      }

      let params = new HttpParams()
        .set('currentPage', currentPage.toString())
        .set('perpage', perpage.toString());

      if (search) {
        params = params.set('search', search);
      }

      const res = await this._http.get(`${this.apiUri}/cabinet/${idProfil}`, {...this.defaultHttpOptions, params}).toPromise();
      const result: Paginated<ModeleOrdonnanceResume> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: res.body as ModeleOrdonnanceResume[]
      };

      this.modelesCabinetCache.set(key, result);
      return result;
    } catch (error) {
      this._snack.openSnackBar(error?.error?.message || 'Erreur lors de la recherche des modèles.');
      throw error;
    }
  }

  public async findOne(id: string): Promise<ModeleOrdonnanceDetail> {
    try {
      const res = await this._http.get(`${this.apiUri}/${id}`, this.defaultHttpOptions).toPromise();
      return res.body as ModeleOrdonnanceDetail;
    } catch (error) {
      if (error.status === 404) {
        this._snack.openSnackBar('Ce modèle n’existe plus.');
        this.clearAll();
        return;
      }
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async create(nom: string, lignes: LigneOrdonnance[]): Promise<string | undefined> {
    try {
      const lignesCopie: LigneOrdonnance[] = (lignes || []).map(ligne => ({
        ...ligne,
        posologieLigneOrdonnance: ligne.posologieLigneOrdonnance
          ? ligne.posologieLigneOrdonnance.map(posologie => ({ ...posologie }))
          : []
      }));

      const modele: CreateModeleOrdonnance = {
        nom: nom.trim(),
        ligne: lignesCopie
      };

      if (modele.ligne.length > 0) {
        for (const ligne of modele.ligne) {
          this.normalizeLigneOrdonnance(ligne);
        }
      }

      const res = await this._http.post(
        this.apiUri,
        modele,
        this.defaultHttpOptions
      ).toPromise();

      this.clearPerso();
      this._modelesChanged.next();

      return res.body as string;
    } catch (error) {
      if (error?.status === 409) {
        this.clearPerso();
        throw error;
      }

      this._snack.openSnackBar(
        error?.error?.message || 'Erreur lors de la création du modèle.'
      );

      return undefined;
    }
  }

  public async replace(id: string, lignes: LigneOrdonnance[]): Promise<boolean> {
    try {
      if (lignes && lignes.length > 0) {
        for (const ligne of lignes) {
          this.normalizeLigneOrdonnance(ligne);
        }
      }

      await this._http.put(`${this.apiUri}/${id}`, { ligne: lignes || [] }, this.defaultHttpOptions).toPromise();

      this.clearPerso();
      this._modelesChanged.next();
      return true;
    } catch (error) {
      this._snack.openSnackBar(error?.error?.message || 'Erreur lors du remplacement du modèle.');
      return false;
    }
  }

  public async delete(id: string) {
    try {
      await this._http.delete(`${this.apiUri}/${id}`, this.defaultHttpOptions).toPromise();

      this.clearPerso();
      this._modelesChanged.next();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public clearPerso(): void {
    this.modelesPersoCache.clear();
  }

  public clearProfilCabinet(idProfil: string): void {
    if (!idProfil) return;

    for (const key of Array.from(this.modelesCabinetCache.keys())) {
      if (key.startsWith(`${idProfil}_`)) {
        this.modelesCabinetCache.delete(key);
      }
    }
  }

  public clearCabinet(): void {
    this.profilsCabinetCache = undefined;
    this.modelesCabinetCache.clear();
  }

  public clearAll(): void {
    this.clearPerso();
    this.clearCabinet();
  }

  private getCacheKey(currentPage: number, perpage: number, search?: string): string {
    return `${currentPage}_${perpage}_${(search || '').trim().toLowerCase()}`;
  }

  private normalizeLigneOrdonnance(produit: LigneOrdonnance): void {
    const stringKeys: (keyof LigneOrdonnance)[] = [
      'id', 'idOrdonnanceMedicamenteuse', 'idProduit', 'labelProduit',
      'idDcMolecule', 'libelleDcMolecule', 'ingredientProduit',
      'atcNiveau1', 'atcNiveau2', 'atcNiveau3', 'atcNiveau4', 'atcNiveau5',
      'motifNonSubstitution', 'quantiteTotale', 'uniteBoite',
      'dateDebutPrise', 'dateFinPrise', 'commentaireDebut', 'commentaireFin',
      'pharmacieDelivrante', 'renouvellement', 'posologie',
      'prix', 'prixLigne', 'motif', 'typeMotif', 'cim10',
      'codeAld', 'codeMedicabase', 'moleculeEprescription',
      'codeLpp', 'acl', 'ref13', 'libelleMarque', 'conditionnementQuantite',
      'produitLibreTitre', 'cip', 'detailsPreparationMagistrale', 'type'
    ];

    for (const key of stringKeys) {
      if (produit[key] != null) {
        (produit as unknown as Record<string, unknown>)[key] = String(produit[key]);
      }
    }
  }

  private checkCacheProfil(): void {
    const idProfil = this._login.getCurrentProfil()?.id;

    if (this.cacheProfilId !== idProfil) {
      this.clearAll();
      this.cacheProfilId = idProfil;
    }
  }
}
